/**
 * Image Cache Utility
 * Uses IndexedDB to persistently cache images across page refreshes
 */

const DB_NAME = 'ImageCacheDB'
const DB_VERSION = 1
const STORE_NAME = 'images'
const CACHE_EXPIRY_DAYS = 7 // Cache images for 7 days

let db = null
const memoryCache = new Map() // In-memory cache for faster access

/**
 * Initialize the IndexedDB database
 */
function initDB() {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db)
      return
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onerror = () => {
      console.warn('ImageCache: IndexedDB not available, using memory cache only')
      resolve(null)
    }

    request.onsuccess = (event) => {
      db = event.target.result
      resolve(db)
    }

    request.onupgradeneeded = (event) => {
      const database = event.target.result
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        const store = database.createObjectStore(STORE_NAME, { keyPath: 'url' })
        store.createIndex('timestamp', 'timestamp', { unique: false })
      }
    }
  })
}

/**
 * Generate a cache key from URL (normalize and remove query params for consistency)
 */
function getCacheKey(url) {
  try {
    const urlObj = new URL(url)
    // Keep the essential parts: origin + pathname
    return urlObj.origin + urlObj.pathname
  } catch {
    return url
  }
}

function isRemoteHttpUrl(url) {
  try {
    const urlObj = new URL(url, window.location.origin)
    return (urlObj.protocol === 'http:' || urlObj.protocol === 'https:') && urlObj.origin !== window.location.origin
  } catch {
    return false
  }
}

function toDisplayUrl(url) {
  try {
    const urlObj = new URL(url, window.location.origin)
    return urlObj.toString()
  } catch {
    return url
  }
}

function toProxyUrl(url) {
  try {
    const urlObj = new URL(url, window.location.origin)
    if (urlObj.origin === 'https://ap.knowit.today' || urlObj.origin === 'http://ap.knowit.today') {
      return `/ap-asset${urlObj.pathname}${urlObj.search}`
    }
    return urlObj.toString()
  } catch {
    return url
  }
}

/**
 * Check if cached item has expired
 */
function isExpired(timestamp) {
  const expiryTime = CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000
  return Date.now() - timestamp > expiryTime
}

/**
 * Get image from cache (memory first, then IndexedDB)
 */
async function getFromCache(url) {
  const cacheKey = getCacheKey(url)

  // Check memory cache first (fastest)
  if (memoryCache.has(cacheKey)) {
    const cached = memoryCache.get(cacheKey)
    if (!isExpired(cached.timestamp)) {
      return cached.blobUrl
    }
    // Expired, remove from memory
    URL.revokeObjectURL(cached.blobUrl)
    memoryCache.delete(cacheKey)
  }

  // Check IndexedDB
  try {
    await initDB()
    if (!db) return null

    return new Promise((resolve) => {
      const transaction = db.transaction([STORE_NAME], 'readonly')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.get(cacheKey)

      request.onsuccess = () => {
        const result = request.result
        if (result && !isExpired(result.timestamp)) {
          // Create blob URL and store in memory cache
          const blobUrl = URL.createObjectURL(result.blob)
          memoryCache.set(cacheKey, {
            blobUrl,
            timestamp: result.timestamp
          })
          resolve(blobUrl)
        } else {
          // Expired or not found
          if (result) {
            // Clean up expired entry
            deleteFromCache(cacheKey)
          }
          resolve(null)
        }
      }

      request.onerror = () => resolve(null)
    })
  } catch {
    return null
  }
}

/**
 * Save image to cache (both memory and IndexedDB)
 */
async function saveToCache(url, blob) {
  const cacheKey = getCacheKey(url)
  const timestamp = Date.now()

  // Save to memory cache
  const blobUrl = URL.createObjectURL(blob)
  memoryCache.set(cacheKey, { blobUrl, timestamp })

  // Save to IndexedDB
  try {
    await initDB()
    if (!db) return blobUrl

    return new Promise((resolve) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite')
      const store = transaction.objectStore(STORE_NAME)

      store.put({
        url: cacheKey,
        blob,
        timestamp
      })

      transaction.oncomplete = () => resolve(blobUrl)
      transaction.onerror = () => resolve(blobUrl)
    })
  } catch {
    return blobUrl
  }
}

/**
 * Delete image from cache
 */
async function deleteFromCache(cacheKey) {
  try {
    if (!db) return

    const transaction = db.transaction([STORE_NAME], 'readwrite')
    const store = transaction.objectStore(STORE_NAME)
    store.delete(cacheKey)
  } catch {
    // Ignore errors
  }
}

/**
 * Fetch image with caching
 * Returns a blob URL that can be used as img src
 */
export async function getCachedImage(url) {
  if (!url) return null

  const displayUrl = toDisplayUrl(url)
  const fetchUrl = isRemoteHttpUrl(displayUrl) ? toProxyUrl(displayUrl) : displayUrl

  // Check cache first
  const cached = await getFromCache(fetchUrl)
  if (cached) {
    return cached
  }

  // Fetch from network
  try {
    const response = await fetch(fetchUrl, {
      mode: 'cors',
      credentials: 'omit'
    })

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`)
    }

    const blob = await response.blob()

    // Validate it's an image
    if (!blob.type.startsWith('image/')) {
      console.warn('ImageCache: Not an image:', blob.type)
      return displayUrl
    }

    // Save to cache and return blob URL
    const blobUrl = await saveToCache(fetchUrl, blob)
    return blobUrl
  } catch (error) {
    console.warn('ImageCache: Failed to fetch image:', displayUrl, error)
    return displayUrl
  }
}

/**
 * Preload multiple images into cache
 */
export async function preloadImages(urls) {
  const uniqueUrls = [...new Set(urls.filter(Boolean))]
  return Promise.all(uniqueUrls.map(url => getCachedImage(url)))
}

/**
 * Clear all cached images
 */
export async function clearImageCache() {
  // Clear memory cache
  memoryCache.forEach(({ blobUrl }) => URL.revokeObjectURL(blobUrl))
  memoryCache.clear()

  // Clear IndexedDB
  try {
    await initDB()
    if (!db) return

    const transaction = db.transaction([STORE_NAME], 'readwrite')
    const store = transaction.objectStore(STORE_NAME)
    store.clear()
  } catch {
    // Ignore errors
  }
}

/**
 * Get cache statistics
 */
export async function getCacheStats() {
  const stats = {
    memoryCount: memoryCache.size,
    indexedDBCount: 0,
    totalSize: 0
  }

  try {
    await initDB()
    if (!db) return stats

    return new Promise((resolve) => {
      const transaction = db.transaction([STORE_NAME], 'readonly')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.getAll()

      request.onsuccess = () => {
        const items = request.result || []
        stats.indexedDBCount = items.length
        stats.totalSize = items.reduce((sum, item) => sum + (item.blob?.size || 0), 0)
        resolve(stats)
      }

      request.onerror = () => resolve(stats)
    })
  } catch {
    return stats
  }
}

// Initialize DB on module load
initDB()

export default {
  getCachedImage,
  preloadImages,
  clearImageCache,
  getCacheStats
}
